/* Карточка сделки. ТЗ 6.5, 8.
   Правится сделка той же формой, что и создаётся (/edit/deals/:id) — карточка
   только показывает и двигает по воронке. */

import {
  canEdit, crmLevel, dealColorScope, dealStagesOf, dealStreamOf, humanDate, rub, stageColor, stageTint,
} from "@mera/shared";
import { List, ListButton } from "konsta/react";
import React from "react";
import { useNavigate, useParams } from "../lib/router.jsx";

import Attachments from "../components/Attachments.jsx";
import Feed from "../components/Feed.jsx";
import { Ion } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelEntityCard } from "../components/skeleton.jsx";
import {
  Empty, ErrorState, Metric, MetricGrid, Row, Sep, StageChips, TierBadge,
} from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp, useFetch } from "../lib/store.jsx";
import { alertDialog, confirmDialog, haptic, openLink } from "../lib/tg.js";

export default function DealCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me, today, meta, reloadAlerts } = useApp();
  const { data: d, loading, error, refetch } = useFetch(`/deals/${id}`);

  if (loading) return <Screen back><SkelEntityCard /></Screen>;
  if (error) return <Screen back><ErrorState text={error} onRetry={refetch} /></Screen>;

  /* Этапы берём из настроек бизнеса, а не из зашитого справочника, — и той
     воронки, где сделка живёт: у партнёров свой набор */
  const business = (meta?.businesses || []).find((b) => b.id === d.funnel);
  const stream = dealStreamOf(d);
  const stages = dealStagesOf(business, stream);
  // «Только чтение» по воронке: карточка смотрится, но не правится
  const canWrite = canEdit(crmLevel(me, d.funnel, stream));

  const patch = async (body) => {
    await api.patch(`/deals/${id}`, body);
    haptic.success();
    refetch();
    reloadAlerts();
  };

  const remove = async () => {
    const ok = await confirmDialog(`Удалить сделку «${d.company}»? Вместе с ней удалятся её задачи и заметки.`);
    if (!ok) return;
    await api.del(`/deals/${id}`);
    haptic.success();
    navigate(-1);
  };

  /* Перенос между воронками: клиент ↔ партнёр. Кнопка есть, только если у бизнеса
     заведена вторая воронка и на неё есть права */
  const other = stream === "partners" ? "clients" : "partners";
  const canMove = canWrite && dealStagesOf(business, other).length > 0 && canEdit(crmLevel(me, d.funnel, other));
  const moveStream = async () => {
    const ok = await confirmDialog(
      other === "partners"
        ? `Перенести «${d.company}» в партнёры? Сделка встанет на первый этап воронки партнёров.`
        : `Перенести «${d.company}» в клиенты? Сделка встанет на первый этап воронки продаж.`
    );
    if (!ok) return;
    try {
      await api.post(`/deals/${id}/move-stream`, { stream: other });
      haptic.success();
      refetch();
      reloadAlerts();
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    }
  };

  const toAccounting = async () => {
    const res = await api.post(`/deals/${id}/move-to-accounting`);
    haptic.success();
    navigate(`/project/${res.projectId}`);
  };

  const edit = () => { haptic.light(); navigate(`/edit/deals/${id}`); };
  const editContact = (cid) => { haptic.light(); navigate(`/edit/contacts/${cid}?deal=${id}`); };

  return (
    <Screen back>
      {/* Шапка: тир в рост двух строк, справа дата и направление */}
      <div className="px-4 pt-1">
        <div className="card-tg px-4 py-3">
          <div className="flex items-center gap-3">
            <TierBadge tier={d.tier} size={44} />
            <div className="min-w-0 flex-1">
              <div className="display text-[19px] font-semibold leading-tight truncate">{d.company}</div>
              <div className="text-[13px] truncate" style={{ color: "var(--tg-hint)" }}>
                {stream === "partners" ? "Партнёр" : d.projType || "Тип работ не указан"}
              </div>
            </div>
            <div className="shrink-0 text-right text-[12px]" style={{ color: "var(--tg-hint)" }}>
              {d.added ? <div className="num">{humanDate(d.added)}</div> : null}
              <div className="font-medium truncate max-w-[120px]">{d.biz}</div>
            </div>
          </div>

          {/* Изменить — общая форма сделки; рядом перевод в аккаунтинг — только
              у клиентов: у партнёров проекта нет. В чтении кнопок нет: обе меняют данные */}
          {canWrite ? (
          <div className="mt-3 flex gap-2">
            <button
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-[14px] font-semibold active:opacity-70"
              style={{ background: "color-mix(in srgb, var(--tg-button) 12%, transparent)", color: "var(--tg-button)" }}
              onClick={edit}
            >
              <Ion.pencil size={14} />
              Изменить
            </button>
            {stream !== "partners" ? (
              <button
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-[14px] font-semibold active:opacity-70"
                style={{ background: "color-mix(in srgb, var(--tg-button) 12%, transparent)", color: "var(--tg-button)" }}
                onClick={toAccounting}
              >
                <Ion.swapHorizontal size={14} />
                В аккаунтинг
              </button>
            ) : null}
          </div>
          ) : null}
        </div>
      </div>

      {/* Этап — чипами под шапкой, цветом своей колонки, как на карточке разработки */}
      <StageChips
        stages={stages}
        value={d.status}
        onChange={(s) => { if (!canWrite) return; haptic.select(); patch({ status: s }); }}
        colorOf={(s, i) => stageTint(business, dealColorScope(stream), s, stageColor(i, stages.length))}
      />

      {/* Дальше блоки без заголовков — разделяются только отступом.
          Деньги и поля правятся общей формой сделки. */}
      <MetricGrid cols={3} className="pt-3">
        <Metric label="Выручка" value={rub(d.revenue)} onClick={canWrite ? edit : undefined} />
        <Metric label="Прогн. прибыль" value={rub(d.profit)} color="var(--fin-green)" onClick={canWrite ? edit : undefined} />
        <Metric label="MRR" value={d.mrr ? rub(d.mrr) : "—"} color="var(--fin-blue)" onClick={canWrite ? edit : undefined} />
      </MetricGrid>

      <div className="px-4 pt-2">
        <div className="card-tg py-1" onClick={canWrite ? edit : undefined} role={canWrite ? "button" : undefined}>
          <Row label="Ответственный">{d.owner || "—"}</Row>
          <Sep />
          <Row label="Источник">{d.src || "—"}</Row>
          <Sep />
          <Row label="ЛПР">{d.lpr || "—"}</Row>
        </div>
      </div>

      {/* Контакты: слева кто это, справа стопкой способы связи. Строка открывает
          форму контакта, новый заводится кнопкой внизу блока — у сделки их сколько угодно */}
      <div className="px-4 pt-2">
        <div className="card-tg py-1">
          {!d.contacts.length && !canWrite ? (
            <Empty text="Контактов нет" icon={Ion.person} />
          ) : null}
          {d.contacts.map((c, i) => (
            <React.Fragment key={c.id}>
              {i > 0 ? <Sep /> : null}
              <ContactRow c={c} onEdit={canWrite ? () => editContact(c.id) : undefined} />
            </React.Fragment>
          ))}
          {canWrite ? (
            <>
              {d.contacts.length ? <Sep /> : null}
              <button
                className="w-full px-4 py-2.5 flex items-center gap-2 text-[15px] font-medium active:opacity-60"
                style={{ color: "var(--tg-link)" }}
                onClick={() => editContact("new")}
              >
                <Ion.personAdd size={17} />
                Добавить контакт
              </button>
            </>
          ) : null}
        </div>
      </div>

      {/* Вложения: файлы грузятся с устройства и живут при сделке */}
      <Attachments base={`/deals/${id}`} items={d.atts || []} onChanged={refetch} canEdit={canWrite} />

      {/* Задачи и заметки — фид рисует свои разделы сам */}
      <Feed scope="deals" id={id} items={d.feed} today={today} onChanged={refetch} canEdit={canWrite} />

      {/* Перенос в другую воронку — редкое действие, поэтому внизу, а не в шапке */}
      {canMove ? (
        <List strong inset>
          <ListButton onClick={moveStream}>
            {other === "partners" ? "Перенести в партнёры" : "Перенести в клиенты"}
          </ListButton>
        </List>
      ) : null}

      {/* Удаление — внизу, отдельным блоком по дизайн-системе (как в формах) */}
      {canWrite ? (
        <List strong inset>
          <ListButton colors={{ textIos: "text-[color:var(--fin-red)]" }} onClick={remove}>
            Удалить сделку
          </ListButton>
        </List>
      ) : null}
    </Screen>
  );
}

/* Строка контакта. Ссылки кликаются сами по себе (сайты — через Telegram, иначе
   мини-апп подменил бы себя страницей), поэтому нажатие по ним
   не открывает форму — иначе позвонить или написать было бы нельзя */
function ContactRow({ c, onEdit }) {
  const stop = (e) => e.stopPropagation();
  const link = "inline-flex items-center gap-1 text-[13px] font-semibold max-w-full";
  const href = (u) => (/^[a-z]+:/i.test(u) ? u : `https://${u}`);
  return (
    <div
      className={`px-4 py-2.5 flex items-start gap-3 ${onEdit ? "active:opacity-60" : ""}`}
      role={onEdit ? "button" : undefined}
      onClick={onEdit}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-medium truncate">{c.name}</div>
        <div className="text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
          {c.role || "—"}
        </div>
      </div>
      <div className="shrink-0 min-w-0 max-w-[60%] flex flex-col items-end gap-0.5" style={{ color: "var(--tg-link)" }}>
        {c.tg ? (
          <a href={`https://t.me/${c.tg.replace("@", "")}`} className={link} onClick={stop}>
            <Ion.paperPlane size={12} /> <span className="truncate">{c.tg}</span>
          </a>
        ) : null}
        {c.phone ? (
          <a href={`tel:${c.phone}`} className={`num ${link}`} onClick={stop}>
            <Ion.call size={12} /> <span className="truncate">{c.phone}</span>
          </a>
        ) : null}
        {c.email ? (
          <a href={`mailto:${c.email}`} className={link} onClick={stop}>
            <Ion.mail size={12} /> <span className="truncate">{c.email}</span>
          </a>
        ) : null}
        {(c.links || []).map((u) => (
          <a key={u} href={href(u)} className={link}
            onClick={(e) => { stop(e); e.preventDefault(); openLink(href(u)); }}>
            <Ion.link size={12} /> <span className="truncate">{u.replace(/^https?:\/\//, "")}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
